import { useRef } from "react";
import { clockOf } from "./rules/format";
import { useReviewClock } from "./clock";
import { analysisOf, toneOf } from "./constants";

/*
 * The timeline under the player: the round's modules, then a lane for each
 * analysis that ran with a mark for every check (flagged ones larger, coloured
 * by severity), and the analyses the round did not run ("off"). What the
 * platform never captures is a chip on the audio line under it. On the audio lane, the
 * questions answered aloud are shaded (speech there is expected) and every
 * stretch of detected speech is drawn thin under the marks. A click
 * anywhere seeks the clock; a mark picks its item.
 *
 * @param {Object} props
 * @param {Array} props.modules      parts: {key, name, from, to}
 * @param {Array} props.analyses     the analyses that ran (constants.ANALYSES entries)
 * @param {Array} props.off          the ones that did not
 * @param {Array} props.items        every check (no video items)
 * @param {Array} [props.expected]   stretches where speech is expected: {key, from, to, label}
 * @param {Array} [props.speech]     every stretch of detected speech (audio.speech): {at, dur}
 * @param {string|null} props.sel
 * @param {Function} props.onPick
 * @param {string} props.filter      "any" or an analysis key
 * @param {Function} props.onFilter
 */
export default function Lanes({ modules, analyses, off, items, expected = [], speech = [], sel, onPick, filter, onFilter }) {
  const c = useReviewClock();
  const L = Math.max(1, c.length);
  const pc = (x) => `${Math.max(0, Math.min(100, (x / L) * 100))}%`;
  const area = useRef(null);

  const seekAt = (e) => {
    if (e.target.closest && e.target.closest("button")) return;
    const r = area.current.getBoundingClientRect();
    if (!r.width) return;
    c.seek(Math.round(((e.clientX - r.left) / r.width) * c.length));
  };

  const tickClass = (x) => [
    "proctoring-review__tick",
    `proctoring-review__tick--${x.severity}`,
    x.approx ? "proctoring-review__tick--approx" : "",
    sel === x.key ? "is-selected" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className="proctoring-review__lanes">
      <div className="proctoring-review__lane-labels">
        <span className="proctoring-review__lane-label proctoring-review__lane-label--head">Modules</span>
        {analyses.map((a) => {
          const checks = items.filter((x) => analysisOf(x.kind) === a.k);
          const mine = checks.filter((x) => x.flagged);
          const crit = mine.some((x) => x.severity === "critical");
          // ✓ only when checks ran and came back clear; an analysis that is on but captured
          // nothing (e.g. no camera captures) says so rather than looking clean
          const none = !checks.length;
          return (
            <button
              key={a.k}
              type="button"
              className={`proctoring-review__lane-label proctoring-review__lane-btn${filter === a.k ? " is-on" : ""}`}
              title={none ? `${a.n}: turned on, but nothing was captured` : `${a.n}: ${a.d}`}
              onClick={() => onFilter(a.k)}
              aria-pressed={filter === a.k}
            >
              {a.short}
              {none
                ? <b className="proctoring-review__ink--none">none</b>
                : <b className={crit ? "proctoring-review__ink--bad" : mine.length ? "proctoring-review__ink--warn" : "proctoring-review__ink--ok"}>{mine.length || "✓"}</b>}
            </button>
          );
        })}
        {off.map((a) => (
          <span key={a.k} className="proctoring-review__lane-label proctoring-review__lane-label--off" title={`${a.n} is not part of this round's set-up`}>
            {a.short}
            <b>off</b>
          </span>
        ))}
      </div>
      <div className="proctoring-review__lane-area" ref={area} onClick={seekAt} role="presentation">
        <div className="proctoring-review__mod-row">
          {modules.map((m, i) => {
            const tone = toneOf(i);
            return (
              <button
                key={`${m.key}-${m.from}`}
                type="button"
                className="proctoring-review__mod"
                style={{ left: pc(m.from), width: `calc(${pc(m.to - m.from)} - 2px)`, background: tone.soft, color: tone.ink, boxShadow: `inset 3px 0 0 ${tone.tone}`, "--mod-hover": tone.softHover }}
                onClick={() => c.seek(m.from)}
                title={`${m.name}: ${clockOf(m.from)} to ${clockOf(m.to)}`}
              >
                {m.name}
              </button>
            );
          })}
        </div>
        {analyses.map((a) => (
          <div key={a.k} className="proctoring-review__lane" data-lane={a.k}>
            {a.k === "audio" && expected.map((q) => (
              <span
                key={`exp-${q.key}`}
                className="proctoring-review__lane-expected"
                style={{ left: pc(q.from), width: pc(q.to - q.from) }}
                title={`${q.label}: answered aloud, so speech is expected here`}
              />
            ))}
            {a.k === "audio" && speech.map((x) => (
              <span
                key={`sp-${x.at}`}
                className="proctoring-review__lane-speech"
                style={{ left: pc(x.at), width: pc(Math.max(x.dur, L / 400)) }}
                aria-hidden="true"
              />
            ))}
            {items.filter((x) => analysisOf(x.kind) === a.k).map((x) => (
              <button
                key={x.key}
                type="button"
                data-key={x.key}
                className={tickClass(x)}
                onClick={() => onPick(x)}
                title={`${x.approx ? "About " : ""}${clockOf(x.at)} ${x.what}`}
                aria-label={`${clockOf(x.at)} ${x.what}`}
                style={{ left: pc(x.at), ...(x.flagged && x.dur > 15 ? { width: pc(x.dur) } : {}) }}
              />
            ))}
          </div>
        ))}
        {off.map((a) => <div key={a.k} className="proctoring-review__lane proctoring-review__lane--off" data-lane={a.k} />)}
        <span className="proctoring-review__head" style={{ left: pc(c.t) }} aria-hidden="true"><i>{clockOf(c.t)}</i></span>
      </div>
    </div>
  );
}
